import { useMutation } from '@apollo/client'
import { UpdateSheetCustomModelPropertyDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-base'
import type { ViewQueryNode } from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { useBooleanState } from '@salescore/frontend-common'
import { Button, Form, Input, message, Select } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import { useRecoilState } from 'recoil'

import { sheetCustomModelsAtom } from '../../../../recoil/view/atoms'

interface FormValue {
  label: string
  // type: AvailableType;
  selectOptionsText: string[] | undefined
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function SheetCustomModelPropertyEditForm({
  node,
  propertyName,
}: {
  node: ViewQueryNode
  propertyName: string
}) {
  const [sheetCustomModels, setSheetCustomModels] = useRecoilState(sheetCustomModelsAtom)
  const sheetCustomModel = sheetCustomModels.find((x) => x.name === node.name)
  const property = sheetCustomModel?.properties.find((x) => x.name === propertyName)
  const [updateSheetCustomModelProperty] = useMutation(UpdateSheetCustomModelPropertyDocument)
  const [form] = useForm<FormValue>()
  // eslint-disable-next-line sonarjs/deprecation
  const loading = useBooleanState()

  if (sheetCustomModel === undefined || property === undefined) {
    // ありえないはず
    return <div>{t(`エラー。{{propertyName}}が見つかりません`, { propertyName })}</div>
  }

  return (
    <div
      onKeyDown={(e) => {
        e.stopPropagation()
      }}
    >
      <Form<FormValue>
        layout="vertical"
        form={form}
        className="p-4"
        initialValues={{
          label: property.label,
          selectOptionsText: property.selectOptions?.map((x) => x.value),
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onFinish={async (value) => {
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: 同じlabelは追加できないようにする？
          try {
            loading.setTrue()
            const { data: result } = await updateSheetCustomModelProperty({
              variables: {
                organizationId: getOrganizationIdFromPath(),
                input: {
                  id: sheetCustomModel.id,
                  propertyName: property.name,
                  label: value.label,
                  selectOptions: value.selectOptionsText?.map((value) => ({ value, label: value })) ?? [],
                },
              },
            })
            const model = result?.updateSheetCustomModelProperty
            if (model === undefined) {
              // eslint-disable-next-line sonarjs/todo-tag
              void message.error(t(`追加に失敗しました`)) // TODO
              return
            }

            // modelの内容を更新
            setSheetCustomModels((xs) => Array.dedupeWith([model, ...xs], (a, b) => a.name === b.name)) // 既にあれば、先頭が優先されるので更新される
            void message.success(t(`カスタムカラムを更新しました`))
          } catch (error) {
            if (error instanceof Error) {
              void message.error(`${t(`エラーが発生しました。`)}${error.message}`)
            } else {
              void message.error(t(t(`エラーが発生しました。`)))
            }
          } finally {
            loading.setFalse()
          }
        }}
      >
        <Form.Item
          name="label"
          required
          label={t(`ラベル`)}
          rules={[{ required: true, message: t(`入力してください`) }]}
        >
          <Input />
        </Form.Item>
        {/* eslint-disable-next-line sonarjs/todo-tag */}
        {/* TODO */}
        {/* <Form.Item name="type" required label="データ型" rules={[{ required: true, message: '入力してください' }]}>
          <Select
            {...disableOnClickOutsideProperties}
            options={options}
            onChange={(value) => {
              setType(value as AvailableType)
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          />
        </Form.Item> */}
        {(property.selectOptions ?? []).length > 0 && (
          <Form.Item
            name="selectOptionsText"
            required
            label={t(`選択肢`)}
            rules={[{ required: true, message: t(`入力してください`) }]}
          >
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder={t(`選択肢を入力`)}
              onChange={(values: string[]) => {
                // 選択肢に前後の空白がある場合は削除し、空文字の選択肢は削除する
                form.setFieldsValue({
                  selectOptionsText: pipe(
                    values,
                    // eslint-disable-next-line sonarjs/super-linear-regex
                    Array.map((value) => value.replaceAll(/^\s+|\s+$/gv, '')),
                    Array.filter((v) => v !== ''),
                    Array.dedupe,
                  ),
                })
              }}
            />
          </Form.Item>
        )}
        <div className="flex flex-row-reverse">
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading.isTrue}>
              {t(`更新`)}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  )
}
